import * as React from 'react'

import { format, parse, isValid } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Controller, useFormContext, type RegisterOptions } from 'react-hook-form'
import { withMask } from 'use-mask-input'

import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { AppIcon } from '@/shared/ui/icons'

import { controlVariants, type ControlSize } from './control-styles'
import { useMergedRef } from './lib/merge-refs'
import { useControlName } from './lib/use-control-name'

/**
 * Campo de data.
 *
 * Cobre **apenas data**. Os fluxos com hora — data+hora e só hora — continuam
 * em `DateInput` (react-datepicker), que já os resolve; reimplementá-los aqui
 * seria refazer um componente que funciona, sem ganho para quem usa.
 *
 * O caso que esta camada resolve é outro: a seleção de data simples usava
 * `<input type="date">` nativo, que não aceita os tokens de controle, muda de
 * aparência a cada navegador e não segue a linguagem visual do resto do
 * formulário.
 *
 * São dois caminhos de entrada de propósito: campo de texto mascarado
 * (`dd/MM/yyyy`) mais um botão que abre o calendário. Só-calendário penaliza a
 * data distante de hoje, que é o caso de data de nascimento.
 *
 * O valor guardado no formulário é uma string `yyyy-MM-dd` — o mesmo formato
 * que o input nativo entregava, para que a troca não mexa no payload.
 */

const VALUE_FORMAT = 'yyyy-MM-dd'
const DISPLAY_FORMAT = 'dd/MM/yyyy'
const DISPLAY_MASK = '99/99/9999'

function toDate(value?: string) {
  if (!value) return undefined
  const parsed = parse(value, VALUE_FORMAT, new Date())
  return isValid(parsed) ? parsed : undefined
}

/**
 * Converte o texto digitado em data, ou `undefined` se ainda não for uma.
 *
 * O ida-e-volta pelo `format` reprova data inexistente: o `parse` do date-fns
 * transborda em silêncio e `31/02/1990` viraria 3 de março.
 */
function fromDisplay(text: string) {
  if (text.length !== DISPLAY_FORMAT.length) return undefined
  const parsed = parse(text, DISPLAY_FORMAT, new Date())
  if (!isValid(parsed)) return undefined
  return format(parsed, DISPLAY_FORMAT) === text ? parsed : undefined
}

/**
 * Ancestral que realmente rola — não o `document`, que sempre tem
 * `overflow: visible` nesta base (o scroll da página é do body do app, não
 * de um container). Genérico de propósito: qualquer consumidor do `DateField`
 * pode estar dentro de um container rolável diferente do modal.
 */
function getScrollParent(node: Element | null): Element | null {
  let current = node?.parentElement ?? null
  while (current) {
    const { overflowY } = getComputedStyle(current)
    if (overflowY === 'auto' || overflowY === 'scroll') return current
    current = current.parentElement
  }
  return null
}

type DatePresentationProps = {
  value?: string
  onValueChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  size?: ControlSize
  className?: string
  'aria-label'?: string
}

function DatePresentation({
  value,
  onValueChange,
  // O texto de espera passa a ser o formato, não um convite a clicar: agora
  // que dá para digitar, quem preenche precisa saber a ordem dos campos.
  placeholder = 'dd/mm/aaaa',
  disabled,
  size,
  className,
  ...rest
}: DatePresentationProps) {
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const selected = toDate(value)

  // A máscara escreve direto no nó, então o input é não-controlado: um `value`
  // do React disputaria a cada tecla com o que a lib acabou de gravar.
  React.useEffect(() => {
    const node = inputRef.current
    if (!node) return

    const display = selected ? format(selected, DISPLAY_FORMAT) : ''
    // Sem esta guarda o efeito apagaria o que está sendo digitado no instante
    // em que a data fica completa e o valor sobe para o formulário.
    if (node.value !== display) node.value = display
  }, [value, selected])

  const maskRef = React.useCallback((node: HTMLInputElement | null) => {
    if (node) withMask(DISPLAY_MASK)(node)
  }, [])

  const mergedInputRef = useMergedRef(maskRef, inputRef)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value

    // Texto incompleto não mexe no formulário: comprometer a cada tecla faria
    // o campo alternar entre datas absurdas enquanto se digita.
    if (text === '') onValueChange('')
    else {
      const parsed = fromDisplay(text)
      if (parsed) onValueChange(format(parsed, VALUE_FORMAT))
    }
  }

  const handleBlur = () => {
    const node = inputRef.current
    if (!node) return

    // Sair com texto que não vira data deixaria a tela mostrando uma coisa e o
    // formulário guardando outra.
    if (node.value !== '' && !fromDisplay(node.value)) {
      node.value = selected ? format(selected, DISPLAY_FORMAT) : ''
    }
  }

  React.useEffect(() => {
    if (!open) return undefined

    // O `PopoverContent` é portado para `document.body`: o rastreio de posição
    // contra um ancestral rolável é frágil o bastante para o calendário se
    // desgrudar do campo durante o scroll. Reabrir é um clique; ficar com o
    // calendário solto no lugar errado não é recuperável.
    const scrollParent = getScrollParent(triggerRef.current)
    if (!scrollParent) return undefined

    const handleScroll = () => setOpen(false)
    scrollParent.addEventListener('scroll', handleScroll)
    return () => scrollParent.removeEventListener('scroll', handleScroll)
  }, [open])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Âncora é o campo inteiro, não o botão: alinha o calendário pela borda
          esquerda do controle, não pelo ícone encostado na direita. */}
      <PopoverAnchor asChild>
        <div ref={triggerRef} className="relative">
          <input
            ref={mergedInputRef}
            type="text"
            inputMode="numeric"
            placeholder={placeholder}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
            className={cn(controlVariants({ size }), 'pr-11', className)}
            // `rest` carrega o `id` e os `aria-*` do `Field.Control`. Vão no
            // input: num wrapper, o `htmlFor` do rótulo apontaria para a `div`.
            {...rest}
          />
          <PopoverTrigger
            type="button"
            disabled={disabled}
            // 36px de alvo — o ícone sozinho tem 18px.
            aria-label="Abrir calendário"
            className="absolute right-1.5 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-[var(--control-r-sm)] text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <AppIcon name="calendar" size={18} />
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={ptBR}
          selected={selected}
          // `getInitialMonth` só olha `month || defaultMonth || today`, nunca
          // `selected`: sem isto, quem já tem data salva reabre o calendário no
          // mês corrente.
          defaultMonth={selected}
          // `--cell-size` dimensiona dia, cabeçalho de semana, setas e a
          // reserva lateral do bloco de mês/ano de uma vez só. O padrão do
          // componente (2rem) espreme "dom/seg/ter/…" e cola os dropdowns
          // nas setas de navegação.
          className="[--cell-size:2.25rem]"
          // Sem isto o `Calendar` cai em `captionLayout="label"`: mês/ano como
          // texto fixo, sem navegação rápida.
          captionLayout="dropdown"
          onSelect={date => {
            onValueChange(date ? format(date, VALUE_FORMAT) : '')
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

type DateFieldProps = Omit<DatePresentationProps, 'value' | 'onValueChange'> & {
  /** Só é necessário fora de `<Field>` — ver `useControlName`. */
  name?: string
  value?: string
  onChange?: (value: string) => void
  rules?: RegisterOptions
}

export const DateField = Object.assign(
  function DateField({ name, value, onChange, rules, ...rest }: DateFieldProps) {
    if (onChange) {
      return <DatePresentation value={value} onValueChange={onChange} {...rest} />
    }

    return <RegisteredDate name={name} rules={rules} {...rest} />
  },
  { displayName: 'DateField' }
)

function RegisteredDate({
  name: nameProp,
  rules,
  ...rest
}: Omit<DateFieldProps, 'value' | 'onChange'>) {
  const name = useControlName(nameProp)
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <DatePresentation
          value={field.value as string}
          onValueChange={field.onChange}
          {...rest}
        />
      )}
    />
  )
}
